import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../../firebase/config";

const AdminCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({
    code: "",
    discountPercent: "",
    maxDiscountAmount: "",
    expiryDate: "",
    usageLimit: "",
    description: "",
  });
  const [editId, setEditId] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchCoupons();
  }, []);

  const fetchCoupons = async () => {
    try {
      setLoading(true);
      const snapshot = await getDocs(collection(db, "coupons"));
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setCoupons(data);
    } catch (error) {
      console.error("Error fetching coupons:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.code || !form.discountPercent || !form.expiryDate) {
      alert("Please fill in all required fields (Code, Discount %, and Expiry Date)");
      return;
    }

    try {
      const payload = {
        ...form,
        code: form.code.toUpperCase().trim(),
        discountPercent: parseInt(form.discountPercent),
        maxDiscountAmount: parseInt(form.maxDiscountAmount) || 0,
        usageLimit: parseInt(form.usageLimit) || 100,
        expiryDate: form.expiryDate,
        description: form.description,
        updatedAt: serverTimestamp(),
      };

      if (editId) {
        // Update existing coupon
        const ref = doc(db, "coupons", editId);
        await updateDoc(ref, payload);
        alert("✅ Coupon updated successfully!");
        setEditId(null);
      } else {
        // Create new coupon
        await addDoc(collection(db, "coupons"), {
          ...payload,
          usedCount: 0,
          isActive: true,
          usedBy: {},
          createdAt: serverTimestamp(),
        });
        alert("✅ Coupon created successfully!");
      }

      // Reset form
      setForm({
        code: "",
        discountPercent: "",
        maxDiscountAmount: "",
        expiryDate: "",
        usageLimit: "",
        description: "",
      });
      
      fetchCoupons();
    } catch (error) {
      console.error("Error saving coupon:", error);
      alert("❌ Failed to save coupon. Please try again.");
    }
  };

  const toggleStatus = async (id, current) => {
    try {
      const ref = doc(db, "coupons", id);
      await updateDoc(ref, { 
        isActive: !current,
        updatedAt: serverTimestamp()
      });
      fetchCoupons();
    } catch (error) {
      console.error("Error updating coupon status:", error);
      alert("❌ Failed to update coupon status.");
    }
  };

  const handleEdit = (coupon) => {
    setEditId(coupon.id);
    setForm({
      code: coupon.code,
      discountPercent: coupon.discountPercent.toString(),
      maxDiscountAmount: coupon.maxDiscountAmount?.toString() || "",
      expiryDate: coupon.expiryDate,
      usageLimit: coupon.usageLimit?.toString() || "",
      description: coupon.description || "",
    });
  };

  const handleCancelEdit = () => {
    setEditId(null);
    setForm({
      code: "",
      discountPercent: "",
      maxDiscountAmount: "",
      expiryDate: "",
      usageLimit: "",
      description: "",
    });
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm("Are you sure you want to delete this coupon?")) return;
    
    try {
      const ref = doc(db, "coupons", id);
      await updateDoc(ref, { 
        isActive: false,
        deletedAt: serverTimestamp()
      });
      fetchCoupons();
      alert("✅ Coupon deleted successfully!");
    } catch (error) {
      console.error("Error deleting coupon:", error);
      alert("❌ Failed to delete coupon.");
    }
  };

  const isExpired = (expiryDate) => {
    return new Date(expiryDate) < new Date();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-green-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-green-700 font-medium">Loading coupons...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-green-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-green-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-green-700">JR Home Made Foods</h1>
              <p className="text-gray-600">Admin Panel</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="p-6 max-w-6xl mx-auto">
        {/* Navigation Breadcrumb */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="text-green-600 hover:text-green-800 mr-2"
          >
            ← Dashboard
          </button>
          <span className="text-gray-400 mx-2">/</span>
          <span className="text-gray-600">Coupons</span>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-green-200 overflow-hidden">
          {/* Header */}
          <div className="bg-green-50 px-6 py-4 border-b border-green-200">
            <h2 className="text-2xl font-bold text-green-700">🎟️ Manage Coupons</h2>
            <p className="text-gray-600">
              {editId ? "Edit existing coupon" : "Create and manage discount coupons for customers"}
            </p>
          </div>

          {/* Create/Edit Coupon Form */}
          <div className="p-6 border-b border-green-100">
            <h3 className="text-lg font-semibold text-green-700 mb-4">
              {editId ? "✏️ Edit Coupon" : "➕ Create New Coupon"}
            </h3>
            
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code *</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., JR25, WELCOME50"
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Discount % *</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  type="number"
                  min="1"
                  max="100"
                  placeholder="25"
                  value={form.discountPercent}
                  onChange={(e) => setForm({ ...form, discountPercent: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Discount (₹)</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  type="number"
                  min="0"
                  placeholder="100"
                  value={form.maxDiscountAmount}
                  onChange={(e) => setForm({ ...form, maxDiscountAmount: e.target.value })}
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date *</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  type="date"
                  value={form.expiryDate}
                  onChange={(e) => setForm({ ...form, expiryDate: e.target.value })}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Usage Limit</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  type="number"
                  min="1"
                  placeholder="100"
                  value={form.usageLimit}
                  onChange={(e) => setForm({ ...form, usageLimit: e.target.value })}
                />
              </div>
              
              <div className="md:col-span-2 lg:col-span-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <input
                  className="w-full p-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  placeholder="e.g., 25% off for new customers"
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
              
              <div className="md:col-span-2 lg:col-span-3 flex gap-3">
                <button
                  type="submit"
                  className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                >
                  {editId ? "✏️ Update Coupon" : "➕ Create Coupon"}
                </button>
                {editId && (
                  <button
                    type="button"
                    onClick={handleCancelEdit}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200 shadow-md hover:shadow-lg"
                  >
                    ❌ Cancel Edit
                  </button>
                )}
              </div>
            </form>
          </div>

          {/* Coupons Table */}
          <div className="p-6">
            <h3 className="text-lg font-semibold text-green-700 mb-4">📋 Existing Coupons</h3>
            
            {coupons.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <div className="text-4xl mb-4">🎟️</div>
                <p>No coupons created yet.</p>
                <p className="text-sm">Create your first coupon using the form above.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Code</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Discount</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Limit</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Used</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Expires</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Status</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {coupons.map((coupon) => (
                      <tr key={coupon.id} className={`border-b hover:bg-gray-50 transition-colors ${
                        isExpired(coupon.expiryDate) ? "bg-red-50" : ""
                      }`}>
                        <td className="px-4 py-3">
                          <div className="font-mono font-semibold text-green-700">{coupon.code}</div>
                          {coupon.description && (
                            <div className="text-xs text-gray-500">{coupon.description}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold">{coupon.discountPercent}%</div>
                          {coupon.maxDiscountAmount > 0 && (
                            <div className="text-xs text-gray-500">Max ₹{coupon.maxDiscountAmount}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">{coupon.usageLimit || '∞'}</td>
                        <td className="px-4 py-3 text-center">
                          {Object.keys(coupon.usedBy || {}).length}
                        </td>
                        <td className="px-4 py-3">
                          <div className={`text-sm ${
                            isExpired(coupon.expiryDate) 
                              ? 'text-red-600 font-semibold' 
                              : 'text-gray-700'
                          }`}>
                            {new Date(coupon.expiryDate).toLocaleDateString()}
                            {isExpired(coupon.expiryDate) && (
                              <span className="text-red-600 text-xs ml-2">(Expired)</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleStatus(coupon.id, coupon.isActive)}
                            className={`px-3 py-1 rounded-full text-xs font-bold transition-all ${
                              coupon.isActive
                                ? 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700'
                                : 'bg-red-100 text-red-700 hover:bg-green-100 hover:text-green-700'
                            }`}
                          >
                            {coupon.isActive ? '✅ Active' : '❌ Inactive'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEdit(coupon)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                              ✏️ Edit
                            </button>
                            <button
                              onClick={() => deleteCoupon(coupon.id)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium"
                            >
                              🗑️ Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminCoupons; 